import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Container, Modal, Button } from 'react-bootstrap';
import axios from 'axios';
import {
  MapPin, Calendar, Hotel, Users, Phone, Mail, X,
  ArrowRight, Package, XCircle, MessageSquare,
  Clock, CheckCircle, Tag, List, Ban,
} from 'lucide-react';
import AppContext from '../../../../context/AppContext';
import CustomerTestimonials from '../../../../screens/customer/CustomerTestimonials/CustomerTestimonials';

const hasValue = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return Boolean(v);
};

const parseJsonField = (field) => {
  if (!field) return [];
  if (Array.isArray(field)) return field;
  try { return JSON.parse(field); } catch { return []; }
};

const formatDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    timeZone: 'UTC',
  });
};

const normalizeStatus = (s = '') => {
  const lower = s.toLowerCase();
  if (lower === 'completed') return 'Completed';
  if (lower === 'confirmed') return 'Confirmed';
  if (lower === 'cancelled' || lower === 'canceled') return 'Cancelled';
  if (lower === 'pending') return 'Pending';
  return s;
};

const getStatusClass = (status) => {
  if (status === 'Completed') return 'cus-ovr-status-completed';
  if (status === 'Confirmed')  return 'cus-ovr-status-confirmed';
  if (status === 'Cancelled')  return 'cus-ovr-status-cancelled';
  return '';
};

const OverviewSection = () => {
  const navigate = useNavigate();
  const { baseUrl, authToken } = useContext(AppContext);
  const customerId = localStorage.getItem('customerId');
  const placeholderImg = "/images/no-image.png";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allBookings, setAllBookings] = useState([]);

  const [showPkgModal, setShowPkgModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [pkgDetail, setPkgDetail] = useState(null);
  const [pkgLoading, setPkgLoading] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewBookingId, setReviewBookingId] = useState(null);

  const [showHotelModal, setShowHotelModal] = useState(false);
  const [selectedHotelBooking, setSelectedHotelBooking] = useState(null);

  const imgSrc = (path) =>
    path ? `${baseUrl.replace('/api', '')}/storage/${path}` : null;
const getHotelInitial = (name) => {
  if (!name) return 'H';
  return name.charAt(0).toUpperCase();
};
const getBookedRoom = (booking) => {
  const allRooms = booking.jjvendor?.jjhotelregistration?.jjrooms || [];
  if (booking.jjroom_id) {
    const match = allRooms.find(room => room.id === booking.jjroom_id);
    if (match) return match;
  }
  return allRooms[0] || {};
};

  useEffect(() => {
    if (!customerId) {
      setError('Customer session not found. Please login again.');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/viewbookingcustomer`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { customer_id: customerId },
      })
      .then((res) => {
        const data = res.data?.data ?? {};
        const merged = [
          ...(data.package_bookings ?? []),
          ...(data.hotel_bookings   ?? []),
        ];
        setAllBookings(merged);
      })
      .catch(() => setError('Unable to load bookings. Please try again.'))
      .finally(() => setLoading(false));
  }, [baseUrl, authToken, customerId]);

  const openPackageDetail = (booking) => {
    setSelectedBooking(booking);
    setShowPkgModal(true);
    setPkgDetail(null);
    setPkgLoading(true);

    axios
      .get(`${baseUrl}/customer/booking-package-detail/${booking.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const d = res.data?.data;
        setPkgDetail(d?.package ?? d?.jjpackage ?? booking.jjpackage ?? null);
      })
      .catch(() => {
        setPkgDetail(booking.jjpackage ?? null);
      })
      .finally(() => setPkgLoading(false));
  };

  const openReviewModal = (booking) => {
    setReviewBookingId(booking.id);
    setShowReviewModal(true);
  };
 const closeReviewModal = () => {
  setShowReviewModal(false);
  setReviewBookingId(null);

  axios
    .get(`${baseUrl}/customer/viewbookingcustomer`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
      params: {
        customer_id: customerId,
      },
    })
    .then((res) => {
      const data = res.data.data;

      setAllBookings([
        ...(data.package_bookings ?? []),
        ...(data.hotel_bookings ?? []),
      ]);
    });
};

  const packageBookings = allBookings.filter((b) => b.jjpackage);
  const hotelBookings   = allBookings.filter((b) => b.jjvendor?.jjhotelregistration);
  const cancelledCount  = allBookings.filter(
    (b) => normalizeStatus(b.booking_status) === 'Cancelled'
  ).length;

  const stats = [
    { label: 'Total Booked Packages', value: packageBookings.length, icon: <Package size={22} />, accent: '#2C5F7C', sub: `${packageBookings.length} packages` },
    { label: 'Total Booked Hotels',   value: hotelBookings.length,   icon: <Hotel size={22} />,   accent: '#4A9E8E', sub: `${hotelBookings.length} hotels`   },
    { label: 'Cancelled Bookings',    value: cancelledCount,         icon: <XCircle size={22} />, accent: '#dc3545', sub: `${cancelledCount} cancelled`       },
  ];

  if (loading) return (
    <div className="pub-cus-loading">
      <div className="pub-cus-loading-spinner" />
      <p>Loading your bookings…</p>
    </div>
  );
  if (error) return (
    <div className="pub-cus-loading">
      <p style={{ color: '#e53e3e' }}>⚠ {error}</p>
    </div>
  );

  return (
    <div className="cus-ovr-dashboard-content">
      <Container fluid className="cus-ovr-container">

        <Row className="mb-4 g-3">
          {stats.map((stat, idx) => (
            <Col xs={12} sm={6} lg={4} key={idx}>
              <Card className="cus-ovr-stat-card">
                <Card.Body className="cus-ovr-card-body cus-ovr-stat-body">
                  <div className="cus-ovr-stat-icon-wrap" style={{ background: stat.accent + '18', color: stat.accent }}>
                    {stat.icon}
                  </div>
                  <div>
                    <h2 className="cus-ovr-stat-number" style={{ color: stat.accent }}>{stat.value}</h2>
                    <h6 className="cus-ovr-stat-label">{stat.label}</h6>
                    <p className="cus-ovr-stat-sub" style={{ color: stat.accent }}>{stat.sub}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        <Card className="cus-ovr-main-card mb-4">
          <Card.Body className="cus-ovr-card-body">
            <div className="cus-ovr-section-header mb-3">
              <h5 className="cus-ovr-section-title mb-0">Booked Packages</h5>
              <button className="cus-ovr-see-all-btn" onClick={() => navigate('/packages')}>
                ALL Packages <ArrowRight size={14} />
              </button>
            </div>

            {packageBookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                <Package size={40} color="#cbd5e1" />
                <p style={{ marginTop: 10 }}>No package bookings yet.</p>
              </div>
            ) : (
              <div className="cus-ovr-table-responsive">
                <table className="cus-ovr-table">
                  <thead>
                    <tr>
                      <th>Package</th>
                      <th>Duration</th>
                      <th>Departure</th>
                      <th>Guests</th>
                      <th>Remaining Price</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packageBookings.map((b) => {
                      const pkg    = b.jjpackage;
                      const status = normalizeStatus(b.booking_status);
                      const dur    = pkg.duration_days && pkg.duration_nights
                        ? `${pkg.duration_days}D / ${pkg.duration_nights}N`
                        : pkg.duration_days ? `${pkg.duration_days} Days` : '—';
                      return (
                        <tr key={b.id} className="cus-ovr-table-row">
                          <td>
                            <div className="cus-ovr-pkg-name-cell">
                              <div className="cus-ovr-tbl-name">{pkg.package_name}</div>
                              <div className="cus-ovr-tbl-dest">
                                <MapPin size={11} /> {pkg.location || pkg.package_destination || '—'}
                              </div>
                            </div>
                          </td>
                          <td><div className="cus-ovr-tbl-date"><Clock size={13} /> {dur}</div></td>
                          <td><div className="cus-ovr-tbl-date"><Calendar size={13} /> {formatDate(pkg.package_date)}</div></td>
                          <td><span className="cus-ovr-tbl-guests"><Users size={13} /> {b.number_of_guests || 1}</span></td>
                          <td><span className="cus-ovr-tbl-price">PKR {Number(b.remaining_amount || 0).toLocaleString()}</span></td>
                          <td><span className={`cus-ovr-status-badge ${getStatusClass(status)}`}>{status}</span></td>
                          <td>
                            <div className="cus-ovr-action-btns">
                              <button className="cus-ovr-view-btn" onClick={() => openPackageDetail(b)}>Details</button>
                              <button
  className={`cus-ovr-review-btn ${
    status === 'Completed' && !b.has_review
      ? 'cus-ovr-review-btn--active'
      : 'cus-ovr-review-btn--disabled'
  }`}
  disabled={status !== 'Completed' || b.has_review}
  onClick={() =>
    status === 'Completed' && !b.has_review && openReviewModal(b)
  }
  title={
    b.has_review
      ? 'Review already submitted'
      : status === 'Completed'
      ? 'Write a Review'
      : 'Available after completion'
  }
>
  <MessageSquare size={13} />
  {b.has_review ? 'Reviewed' : 'Review'}
</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card.Body>
        </Card>

        <Card className="cus-ovr-main-card mb-4">
          <Card.Body className="cus-ovr-card-body">
            <div className="cus-ovr-section-header mb-3">
              <h5 className="cus-ovr-section-title mb-0">Booked Hotels</h5>
              <button className="cus-ovr-see-all-btn" onClick={() => navigate('/hotels')}>
                All Hotels <ArrowRight size={14} />
              </button>
            </div>

            {hotelBookings.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>
                <Hotel size={40} color="#cbd5e1" />
                <p style={{ marginTop: 10 }}>No hotel bookings yet.</p>
              </div>
            ) : (
              <Row className="g-3">
                {hotelBookings.map((b) => {
                  const hotel = b.jjvendor?.jjhotelregistration;
                  const status = normalizeStatus(b.booking_status);
                  const hotelImage = hotel?.hotel_image
                    ? imgSrc(hotel.hotel_image)
                    : placeholderImg;

                  return (
                    <Col xs={12} sm={6} xl={4} key={b.id}>
                      <Card className="cus-ovr-hotel-booking-card">
                        <div className="cus-ovr-hcard-header">
                          {/* ── Hotel image with placeholder fallback ── */}
                          <div className="cus-ovr-hcard-img-wrap">

  {hotel?.hotel_image ? (
    <img
      src={imgSrc(hotel.hotel_image)}
      alt={hotel?.hotel_name || 'Hotel'}
      className="cus-ovr-hcard-img"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
        e.currentTarget.nextSibling.style.display = 'flex';
      }}
    />
  ) : null}


  <div
    className="cus-ovr-hotel-letter"
    style={{
      display: hotel?.hotel_image ? 'none' : 'flex'
    }}
  >
    {getHotelInitial(hotel?.hotel_name)}
  </div>

</div>
                          <span className={`cus-ovr-status-badge ${getStatusClass(status)}`}>{status}</span>
                        </div>
                        <Card.Body className="cus-ovr-card-body">
                          <h6 className="cus-ovr-hcard-name">{hotel?.hotel_name || '—'}</h6>
                          <p className="cus-ovr-hcard-loc"><MapPin size={13} /> {hotel?.hotel_location || hotel?.city || '—'}</p>
                          <div className="cus-ovr-hcard-dates">
                            <div className="cus-ovr-hcard-date-item">
                              <span className="cus-ovr-hcard-date-label">Check-In</span>
                              <span className="cus-ovr-hcard-date-val">{formatDate(b.checkIn)}</span>
                            </div>
                            <div className="cus-ovr-hcard-date-divider" />
                            <div className="cus-ovr-hcard-date-item">
                              <span className="cus-ovr-hcard-date-label">Check-Out</span>
                              <span className="cus-ovr-hcard-date-val">{formatDate(b.checkOut)}</span>
                            </div>
                          </div>
                          <div className="cus-ovr-hcard-footer">
                            <div>
                              <div className="cus-ovr-hcard-guests"><Users size={13} /> {b.number_of_guests || 1} Guest(s)</div>
                              <div className="cus-ovr-hcard-price">PKR {Number(b.total_amount || 0).toLocaleString()}</div>
                            </div>
                            <div className="cus-ovr-action-btns">
                              <button className="cus-ovr-view-btn" onClick={() => { setSelectedHotelBooking(b); setShowHotelModal(true); }}>Details</button>
                             <button
  className={`cus-ovr-review-btn ${
    status === 'Completed' && !b.has_review
      ? 'cus-ovr-review-btn--active'
      : 'cus-ovr-review-btn--disabled'
  }`}
  disabled={status !== 'Completed' || b.has_review}
  onClick={() =>
    status === 'Completed' && !b.has_review && openReviewModal(b)
  }
  title={
    b.has_review
      ? 'Review already submitted'
      : status === 'Completed'
      ? 'Write a Review'
      : 'Available after completion'
  }
>
  <MessageSquare size={13} />
  {b.has_review ? 'Reviewed' : 'Review'}
</button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            )}
          </Card.Body>
        </Card>

      </Container>

      <Modal show={showPkgModal} onHide={() => setShowPkgModal(false)} size="xl" centered className="cus-ovr-modal">
        <Modal.Header className="cus-ovr-modal-header">
          <Modal.Title className="cus-ovr-modal-title">
            {pkgDetail?.package_name || selectedBooking?.jjpackage?.package_name || 'Package Details'}
            {hasValue(pkgDetail?.package_category) && (
              <span style={{ background: 'rgba(255,255,255,0.2)', fontSize: '0.75rem', padding: '3px 10px', borderRadius: 20, marginLeft: 10, fontWeight: 600 }}>
                {pkgDetail.package_category}
              </span>
            )}
          </Modal.Title>
          <button onClick={() => setShowPkgModal(false)} className="cus-ovr-close-btn"><X size={20} /></button>
        </Modal.Header>

        <Modal.Body className="cus-ovr-modal-body">
          {pkgLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem' }}>
              <div className="pub-cus-loading-spinner" />
              <p style={{ marginTop: 12, color: '#6c757d' }}>Loading package details…</p>
            </div>
          ) : (() => {
            const pkg = pkgDetail || selectedBooking?.jjpackage;
            const b = selectedBooking;
            if (!pkg) return <p style={{ textAlign: 'center', padding: '2rem' }}>Package details not available.</p>;

            const inclusions = parseJsonField(pkg.package_inclusion);
            const exclusions = parseJsonField(pkg.package_exclusion);
            const itinerary  = parseJsonField(pkg.package_itinerary);
            const image      = pkg.package_image ? imgSrc(pkg.package_image) : placeholderImg;
            const duration   = pkg.duration_days && pkg.duration_nights
              ? `${pkg.duration_days} Days / ${pkg.duration_nights} Nights`
              : pkg.duration_days ? `${pkg.duration_days} Days` : null;
            const status = normalizeStatus(b?.booking_status);

            return (
              <Row>
                <Col lg={4} className="mb-3">
                  <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 16, background: '#E0F2F1', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img
                      src={image}
                      alt={pkg.package_name || 'Package'}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.currentTarget.src = placeholderImg; }}
                    />
                  </div>
                  <div className="cus-ovr-price-card" style={{ marginBottom: 16 }}>
                    <div className="cus-ovr-price-label">Total Paid</div>
                    <div className="cus-ovr-price-value">PKR {Number(b?.total_amount || pkg.package_price || 0).toLocaleString()}</div>
                    <div style={{ marginTop: 8, opacity: 0.85, fontSize: '0.78rem' }}>
                      Booking #{b?.id} &nbsp;|&nbsp;
                      <span className={`cus-ovr-status-badge ${getStatusClass(status)}`} style={{ fontSize: '0.7rem', padding: '2px 8px' }}>{status}</span>
                    </div>
                  </div>
                  <Row className="g-2">
                    {[
                      { label: 'Location', val: pkg.location || pkg.package_destination, icon: <MapPin size={13} /> },
                      { label: 'Duration', val: duration, icon: <Clock size={13} /> },
                      { label: 'Departure', val: formatDate(pkg.package_date), icon: <Calendar size={13} /> },
                      { label: 'Guests', val: `${b?.number_of_guests || 1} Person(s)`, icon: <Users size={13} /> },
                    ].filter(d => hasValue(d.val)).map((d, i) => (
                      <Col xs={6} key={i}>
                        <div className="cus-ovr-detail-box" style={{ padding: '0.7rem' }}>
                          <div className="cus-ovr-detail-label" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>{d.icon} {d.label}</div>
                          <div className="cus-ovr-detail-value" style={{ fontSize: '0.82rem', marginTop: 2 }}>{d.val}</div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </Col>
                <Col lg={4} className="mb-3">
                  {hasValue(pkg.package_description) && (
                    <div className="cus-ovr-amenities-section">
                      <h6 className="cus-ovr-amenities-title"><Tag size={15} style={{ marginRight: 6 }} />About This Package</h6>
                      <p style={{ fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>{pkg.package_description}</p>
                    </div>
                  )}
                  {hasValue(inclusions) && (
                    <div className="cus-ovr-amenities-section">
                      <h6 className="cus-ovr-amenities-title"><CheckCircle size={15} style={{ marginRight: 6, color: '#4A9E8E' }} />What's Included</h6>
                      <div className="cus-ovr-amenities-list">{inclusions.map((item, i) => <span key={i} className="cus-ovr-amenity-tag">✓ {item}</span>)}</div>
                    </div>
                  )}
                  {hasValue(exclusions) && (
                    <div className="cus-ovr-amenities-section">
                      <h6 className="cus-ovr-amenities-title"><Ban size={15} style={{ marginRight: 6, color: '#dc3545' }} />Not Included</h6>
                      <div className="cus-ovr-amenities-list">{exclusions.map((item, i) => <span key={i} className="cus-ovr-amenity-tag" style={{ borderColor: '#dc3545', color: '#842029', background: '#ffe6e6' }}>✕ {item}</span>)}</div>
                    </div>
                  )}
                </Col>
                <Col lg={4} className="mb-3">
                  {hasValue(itinerary) && (
                    <div className="cus-ovr-amenities-section">
                      <h6 className="cus-ovr-amenities-title"><List size={15} style={{ marginRight: 6 }} />Daily Itinerary</h6>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {itinerary.map((day, i) => (
                          <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 12px', background: '#f8f9fa', borderRadius: 10, border: '1.5px solid #E0F2F1' }}>
                            <div style={{ minWidth: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,#2C5F7C 0%,#4A9E8E 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>D{day.day || i + 1}</div>
                            <div>
                              {hasValue(day.title) && <div style={{ fontWeight: 700, fontSize: '0.84rem', color: '#2C5F7C', marginBottom: 2 }}>{day.title}</div>}
                              {hasValue(day.description) && <div style={{ fontSize: '0.78rem', color: '#6c757d', lineHeight: 1.5 }}>{day.description}</div>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <Card className="cus-ovr-contact-card">
                    <Card.Body className="cus-ovr-card-body">
                      <h6 className="cus-ovr-contact-title">Need Help?</h6>
                      <p className="cus-ovr-contact-item"><Phone size={14} className="cus-ovr-contact-icon" /> +92 300 1234567</p>
                      <p className="cus-ovr-contact-item"><Mail size={14} className="cus-ovr-contact-icon" /> travel@example.com</p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="cus-ovr-modal-footer">
          <Button onClick={() => setShowPkgModal(false)} className="cus-ovr-close-modal-btn">Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showHotelModal} onHide={() => setShowHotelModal(false)} size="lg" centered className="cus-ovr-modal">
        <Modal.Header className="cus-ovr-modal-header">
          <Modal.Title className="cus-ovr-modal-title">
            {selectedHotelBooking?.jjvendor?.jjhotelregistration?.hotel_name || 'Hotel'} — Booking Details
          </Modal.Title>
          <button onClick={() => setShowHotelModal(false)} className="cus-ovr-close-btn"><X size={20} /></button>
        </Modal.Header>
        <Modal.Body className="cus-ovr-modal-body">
          {selectedHotelBooking && (() => {
            const hotel  = selectedHotelBooking.jjvendor?.jjhotelregistration;
            const bookedRoom = getBookedRoom(selectedHotelBooking);

            const status = normalizeStatus(selectedHotelBooking.booking_status);
            const image  = hotel?.hotel_image ? imgSrc(hotel.hotel_image) : placeholderImg;

            return (
              <div className="cus-ovr-modal-content">
                <Card className="cus-ovr-hotel-card">
                  <Card.Body className="cus-ovr-card-body">
                    <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ width: 64, height: 64, borderRadius: 10, overflow: 'hidden', position: 'relative', flexShrink: 0, background: '#E0F2F1' }}>

  {hotel?.hotel_image ? (
    <img
      src={image}
      alt={hotel?.hotel_name || 'Hotel'}
      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
      onError={(e) => {
        e.currentTarget.style.display = 'none';
        const parent = e.currentTarget.parentNode;
        const fallback = parent.querySelector('.hotel-fallback-letter');
        if (fallback) fallback.style.display = 'flex';
      }}
    />
  ) : null}

  <div
    className="hotel-fallback-letter"
    style={{
      width: '100%',
      height: '100%',
      display: hotel?.hotel_image ? 'none' : 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 700,
      fontSize: '1.2rem',
      color: '#2C5F7C',
      background: '#E0F2F1'
    }}
  >
    {getHotelInitial(hotel?.hotel_name)}
  </div>

</div>
                      <div>
                        <h5 className="cus-ovr-hotel-name" style={{ marginBottom: 4 }}>{hotel?.hotel_name || '—'}</h5>
                        <p className="cus-ovr-hotel-location"><MapPin size={14} className="cus-ovr-location-icon" />{hotel?.hotel_location || hotel?.city || '—'}</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
                <Row className="mb-3 g-2">
                  {[
                    { label: 'Check-In',  val: formatDate(selectedHotelBooking.checkIn) },
                    { label: 'Check-Out', val: formatDate(selectedHotelBooking.checkOut) },
                    { label: 'Guests',    val: `${selectedHotelBooking.number_of_guests || 1} Person(s)` },
                    { label: 'Status',    val: status },
                  ].map((d, i) => (
                    <Col md={6} key={i} className="mb-3">
                      <div className="cus-ovr-detail-box">
                        <div className="cus-ovr-detail-label">{d.label}</div>
                        <div className="cus-ovr-detail-value">
                          {d.label === 'Status'
                            ? <span className={`cus-ovr-status-badge ${getStatusClass(d.val)}`}>{d.val}</span>
                            : d.val}
                        </div>
                      </div>
                    </Col>
                  ))}
                </Row>
               {bookedRoom.room_type && (
  <div className="cus-ovr-amenities-section">
    <h6 className="cus-ovr-amenities-title">Room Details</h6>
    <div style={{ background: '#f8f9fa', border: '1.5px solid #E0F2F1', borderRadius: 10, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#2C5F7C' }}>{bookedRoom.room_type}</div>
        {bookedRoom.room_capacity && (
          <div style={{ fontSize: '0.78rem', color: '#6c757d' }}>Capacity: {bookedRoom.room_capacity}</div>
        )}
      </div>
      {bookedRoom.room_price && (
        <span className="cus-ovr-tbl-price">PKR {Number(bookedRoom.room_price).toLocaleString()}</span>
      )}
    </div>
  </div>
)}
                {hasValue(hotel?.hotel_amenities) && (
                  <div className="cus-ovr-amenities-section">
                    <h6 className="cus-ovr-amenities-title">Amenities</h6>
                    <div className="cus-ovr-amenities-list">{parseJsonField(hotel.hotel_amenities).map((a, i) => <span key={i} className="cus-ovr-amenity-tag">✓ {a}</span>)}</div>
                  </div>
                )}
                <div className="cus-ovr-price-card">
                  <div className="cus-ovr-price-label">Remaining Amount</div>
                  <div className="cus-ovr-price-value">PKR {Number(selectedHotelBooking.remaining_amount || 0).toLocaleString()}</div>
                </div>
              </div>
            );
          })()}
        </Modal.Body>
        <Modal.Footer className="cus-ovr-modal-footer">
          <Button onClick={() => setShowHotelModal(false)} className="cus-ovr-close-modal-btn">Close</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showReviewModal} onHide={closeReviewModal} size="md" centered className="cus-ovr-modal">
        <Modal.Header className="cus-ovr-modal-header">
          <Modal.Title className="cus-ovr-modal-title">Write a Review</Modal.Title>
          <button onClick={closeReviewModal} className="cus-ovr-close-btn"><X size={20} /></button>
        </Modal.Header>
        <Modal.Body className="cus-ovr-modal-body" style={{ padding: 0 }}>
          {reviewBookingId && <CustomerTestimonials bookingId={reviewBookingId} onClose={closeReviewModal} />}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default OverviewSection;